import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute(
    '/(onboarding)/(documents)/terms-and-conditions'
)({
    component: TermsAndConditions,
})

function TermsAndConditions() {
    return (
        <>
            <Helmet>
                <title>Terms and Conditions - Style PWA</title>
                <meta
                    name="description"
                    content="Terms and Conditions for Style PWA"
                />
            </Helmet>

            <div className="glass-background min-h-dvh pt-safe">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                            Terms and Conditions
                        </h1>
                        <p className="mt-4 text-xl text-gray-600">
                            Effective Date: October 18, 2025
                        </p>
                        <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                    </div>

                    {/* Content */}
                    <div className="space-y-8">
                        {/* 1. Introduction */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    1
                                </span>
                                Introduction
                            </h2>
                            <div className="space-y-3 leading-relaxed text-gray-700">
                                <p>
                                    Welcome to{' '}
                                    <strong>
                                        What The F*** Are You Wearing
                                    </strong>{' '}
                                    ("<strong>WTFAYW</strong>", "
                                    <strong>the Platform</strong>"), operated by{' '}
                                    <strong>Softwear Labs Pty Ltd</strong> (ACN
                                    691948633) of Suite 2, Level 3, 7-15
                                    Macquarie Place, Sydney 2000 NSW Australia
                                    ("<strong>Softwear Labs</strong>", "
                                    <strong>we</strong>", "<strong>our</strong>
                                    ", or "<strong>us</strong>").
                                </p>
                                <p className="rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-sm">
                                    By accessing or using the Platform or our
                                    mobile applications,{' '}
                                    <strong>
                                        you agree to be bound by these Terms and
                                        Conditions
                                    </strong>{' '}
                                    ("<strong>Terms</strong>").
                                    <strong>
                                        {' '}
                                        If you do not agree, do not use the
                                        Platform.
                                    </strong>
                                </p>
                            </div>
                        </section>

                        {/* 2. Eligibility and Account Registration */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-600">
                                    2
                                </span>
                                Eligibility and Account Registration
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You must be{' '}
                                        <strong>13 years or older</strong> to
                                        create an account. Users under 18
                                        require parental or guardian consent.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You are responsible for{' '}
                                        <strong>
                                            maintaining the confidentiality
                                        </strong>{' '}
                                        of your login credentials and
                                        <strong>
                                            {' '}
                                            all activity under your account
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You may set your profile to{' '}
                                        <strong>public or private</strong> at
                                        any time.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 3. Platform Overview */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-600">
                                    3
                                </span>
                                Platform Overview
                            </h2>
                            <p className="mb-4 text-gray-700">
                                WTFAYW is a social-media-style platform that
                                enables users to:
                            </p>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Import or link fashion and lifestyle
                                        products from any external website.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Create and share digital "collections"
                                        or "looks".
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        View and interact with content posted by
                                        others, subject to their privacy
                                        settings.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Access optional paid features, including
                                        an AI-powered Try-On Feature.
                                    </span>
                                </li>
                            </ul>
                            <p className="mt-4 text-gray-700">
                                All other services are currently provided free
                                of charge.
                            </p>
                        </section>

                        {/* 4. Product Information and Pricing Disclaimer */}
                        <section className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                                    4
                                </span>
                                Product Information and Pricing Disclaimer
                            </h2>
                            <div className="space-y-4">
                                <p className="text-gray-700">
                                    The Platform enables users to import or link
                                    products from third-party websites and to
                                    edit product details such as title,
                                    description, images, or pricing for the
                                    purpose of creating and sharing collections.
                                </p>

                                <div>
                                    <p className="mb-2 font-semibold text-gray-900">
                                        Important Disclaimers:
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Softwear Labs{' '}
                                                <strong>
                                                    does not verify or guarantee
                                                </strong>{' '}
                                                the accuracy, completeness, or
                                                reliability of any product
                                                information displayed on the
                                                Platform.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Product listings, including
                                                prices, availability, and
                                                descriptions, are provided{' '}
                                                <strong>
                                                    for informational and
                                                    illustrative purposes only
                                                </strong>
                                                .
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                When you follow a link to an
                                                external website,{' '}
                                                <strong>
                                                    you are leaving the Platform
                                                </strong>
                                                , and all transactions or
                                                interactions thereafter are
                                                governed by the terms and
                                                policies of that external site.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Product prices shown on the
                                                Platform{' '}
                                                <strong>may differ</strong> from
                                                those displayed by the original
                                                retailer due to factors such as
                                                user input errors, subsequent
                                                price changes, regional pricing,
                                                or promotional variations.
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <p className="mb-2 font-semibold text-gray-900">
                                        Currency Conversion:
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                The Platform provides an
                                                optional currency conversion
                                                feature that converts displayed
                                                prices into other currencies
                                                based on publicly available
                                                exchange-rate data.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                These converted prices are{' '}
                                                <strong>
                                                    intended as an estimate only
                                                </strong>{' '}
                                                and do not represent a binding
                                                or final price.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-amber-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Actual purchase prices may vary
                                                depending on the retailer's own
                                                exchange-rate practices, taxes,
                                                and applicable fees.
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <p className="rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
                                    <strong>
                                        Softwear Labs disclaims all
                                        responsibility
                                    </strong>{' '}
                                    for any losses or discrepancies arising from
                                    reliance on product information, pricing, or
                                    currency conversions displayed within the
                                    Platform.{' '}
                                    <strong>
                                        Users should always confirm details
                                        directly with the original retailer
                                        before making any purchase decisions.
                                    </strong>
                                </p>
                            </div>
                        </section>

                        {/* 5. Premium Subscription and Credits */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-600">
                                    5
                                </span>
                                Premium Subscription and Credits
                            </h2>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                                        Subscription Plans and Pricing
                                    </h3>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                We may offer various
                                                subscription tiers ("
                                                <strong>Plans</strong>")
                                                providing access to additional
                                                features such as AI try-on
                                                credits.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                Plan prices and features are
                                                listed within the Platform and{' '}
                                                <strong>
                                                    are subject to change at any
                                                    time
                                                </strong>
                                                .
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                Any pricing change will{' '}
                                                <strong>
                                                    take effect at the start of
                                                    the next billing cycle
                                                </strong>{' '}
                                                following notice to you.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                We will provide reasonable
                                                advance notice of any change by
                                                email or in-app notification.
                                                Your continued use after the
                                                change takes effect constitutes
                                                acceptance of the new pricing.
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                                        Our Rights to Modify, Suspend, or Cancel
                                        Plans
                                    </h3>
                                    <p className="mb-2 text-gray-700">
                                        Softwear Labs reserves the right to:
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                <strong>
                                                    Modify, discontinue, or
                                                    replace
                                                </strong>{' '}
                                                any Plan or feature at its
                                                discretion;
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                <strong>
                                                    Cancel or suspend
                                                </strong>{' '}
                                                a user's subscription or account
                                                for any breach of these Terms,
                                                misuse of the Platform, or for
                                                operational or security reasons;
                                                and
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                <strong>Terminate</strong> any
                                                Plan or subscription if
                                                continued provision becomes
                                                impractical or commercially
                                                unviable.
                                            </span>
                                        </li>
                                    </ul>
                                    <p className="mt-3 text-sm text-gray-600 italic">
                                        Where practicable, we will give prior
                                        notice of any cancellation or
                                        modification. If a Plan is discontinued,
                                        we may provide equivalent access or a
                                        pro-rated refund at our discretion.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                                        Renewals and Cancellations
                                    </h3>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                Subscriptions{' '}
                                                <strong>
                                                    automatically renew
                                                </strong>{' '}
                                                each billing cycle unless
                                                cancelled before renewal.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                Users may{' '}
                                                <strong>
                                                    cancel at any time
                                                </strong>{' '}
                                                through account settings; access
                                                continues until the end of the
                                                paid period.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-indigo-500">
                                                •
                                            </span>
                                            <span className="text-gray-700">
                                                Except as required by Australian
                                                Consumer Law,{' '}
                                                <strong>
                                                    all fees are non-refundable
                                                </strong>{' '}
                                                once charged.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 6. AI Try-On Feature */}
                        <section className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-blue-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-600">
                                    6
                                </span>
                                AI Try-On Feature
                            </h2>
                            <div className="space-y-4">
                                <p className="text-gray-700">
                                    The Try-On Feature uses{' '}
                                    <strong>
                                        artificial-intelligence technology
                                    </strong>{' '}
                                    to generate simulated imagery.
                                </p>

                                <div>
                                    <p className="mb-2 font-semibold text-gray-900">
                                        Your Responsibilities:
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                You{' '}
                                                <strong>
                                                    must only upload photos of
                                                    yourself
                                                </strong>{' '}
                                                or of others with their{' '}
                                                <strong>
                                                    explicit consent
                                                </strong>
                                                .
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Uploaded photos{' '}
                                                <strong>
                                                    must not contain
                                                </strong>{' '}
                                                nudity, sexually explicit
                                                material, or unlawful,
                                                defamatory, or infringing
                                                content.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                The AI output is{' '}
                                                <strong>
                                                    automatically stored in your
                                                    personal archive
                                                </strong>{' '}
                                                and remains accessible until you
                                                delete it.
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <p className="mb-2 font-semibold text-gray-900">
                                        You acknowledge that:
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                AI outputs are{' '}
                                                <strong>
                                                    generated results, not
                                                    photographs
                                                </strong>
                                                , and may not be accurate or
                                                realistic.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                Softwear Labs{' '}
                                                <strong>
                                                    provides no warranty
                                                </strong>{' '}
                                                as to fitness, likeness, or
                                                satisfaction of any AI output.
                                            </span>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="mt-1 text-cyan-600">
                                                →
                                            </span>
                                            <span className="text-gray-700">
                                                We may{' '}
                                                <strong>
                                                    remove or restrict access
                                                </strong>{' '}
                                                to content deemed inappropriate
                                                or in violation of these Terms.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 7. User Content and Licensing */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-600">
                                    7
                                </span>
                                User Content and Licensing
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        <strong>You retain ownership</strong> of
                                        all content you upload or import.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        By posting or importing content, you
                                        grant Softwear Labs a{' '}
                                        <strong>
                                            non-exclusive, worldwide,
                                            royalty-free, sublicensable, and
                                            transferable licence
                                        </strong>{' '}
                                        to host, reproduce, display, and
                                        distribute your content{' '}
                                        <strong>
                                            solely for the operation and
                                            promotion of the Platform
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You warrant that you{' '}
                                        <strong>own or have the right</strong>{' '}
                                        to post such content and that it{' '}
                                        <strong>
                                            does not infringe any third-party
                                            rights
                                        </strong>
                                        .
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 8. Acceptable Use */}
                        <section className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-orange-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-600">
                                    8
                                </span>
                                Acceptable Use
                            </h2>
                            <p className="mb-3 font-semibold text-gray-900">
                                You must not:
                            </p>
                            <ul className="mb-4 space-y-2">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        Use the Platform for{' '}
                                        <strong>
                                            unlawful, misleading, or harmful
                                            purposes
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        Upload{' '}
                                        <strong>viruses, bots, or code</strong>{' '}
                                        that interferes with the Platform.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        Attempt to{' '}
                                        <strong>
                                            reverse engineer, scrape, or misuse
                                        </strong>{' '}
                                        our APIs or AI systems.
                                    </span>
                                </li>
                            </ul>
                            <p className="rounded border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-900">
                                We reserve the right to{' '}
                                <strong>
                                    suspend or terminate any account
                                </strong>{' '}
                                violating these Terms.
                            </p>
                        </section>

                        {/* 9. Intellectual Property */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-600">
                                    9
                                </span>
                                Intellectual Property
                            </h2>
                            <p className="text-gray-700">
                                <strong>
                                    All intellectual-property rights
                                </strong>{' '}
                                in the Platform, including its software, design,
                                trademarks, and content (other than
                                user-generated content){' '}
                                <strong>
                                    belong to Softwear Labs or its licensors
                                </strong>
                                . You receive{' '}
                                <strong>no ownership rights</strong> beyond the
                                personal, non-exclusive right to use the
                                Platform in accordance with these Terms.
                            </p>
                        </section>

                        {/* 10. Disclaimers and Limitation of Liability */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-sm font-bold text-orange-600">
                                    10
                                </span>
                                Disclaimers and Limitation of Liability
                            </h2>
                            <div className="space-y-3">
                                <p className="text-gray-700">
                                    The Platform and AI features are provided{' '}
                                    <strong>
                                        "as is" without warranties of any kind
                                    </strong>
                                    .
                                </p>
                                <p className="text-gray-700">
                                    <strong>
                                        To the fullest extent permitted by law
                                    </strong>
                                    , Softwear Labs disclaims all liability for:
                                </p>
                                <ul className="space-y-2">
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-orange-500">
                                            •
                                        </span>
                                        <span className="text-gray-700">
                                            Any inaccuracy or defect in AI
                                            outputs;
                                        </span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-orange-500">
                                            •
                                        </span>
                                        <span className="text-gray-700">
                                            Loss of data or content;
                                        </span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-orange-500">
                                            •
                                        </span>
                                        <span className="text-gray-700">
                                            Any indirect, consequential, or
                                            incidental damages.
                                        </span>
                                    </li>
                                </ul>
                                <p className="rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-sm text-blue-900">
                                    <strong>
                                        Nothing in these Terms limits rights
                                        under Australian Consumer Law
                                    </strong>{' '}
                                    that cannot be excluded.
                                </p>
                            </div>
                        </section>

                        {/* 11. Termination */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
                                    11
                                </span>
                                Termination
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-pink-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        <strong>
                                            You may delete your account
                                        </strong>{' '}
                                        at any time.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-pink-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        We may{' '}
                                        <strong>
                                            suspend or terminate access
                                        </strong>{' '}
                                        if you breach these Terms or misuse the
                                        Platform.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-pink-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        Certain clauses (e.g., intellectual
                                        property, liability, governing law){' '}
                                        <strong>survive termination</strong>.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 12. Governing Law and Dispute Resolution */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    12
                                </span>
                                Governing Law and Dispute Resolution
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        These Terms are governed by the{' '}
                                        <strong>
                                            laws of New South Wales, Australia
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        Disputes should first be referred to{' '}
                                        <strong>support@wtf.style</strong> for
                                        informal resolution.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        If unresolved, the parties submit to the{' '}
                                        <strong>
                                            exclusive jurisdiction of the courts
                                            of New South Wales
                                        </strong>
                                        .
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 13. Changes to These Terms */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-600">
                                    13
                                </span>
                                Changes to These Terms
                            </h2>
                            <p className="text-gray-700">
                                We may{' '}
                                <strong>
                                    update these Terms from time to time
                                </strong>{' '}
                                by posting a new version on the Platform.{' '}
                                <strong>
                                    Continued use constitutes acceptance
                                </strong>{' '}
                                of the updated Terms. We may also modify our
                                subscription pricing, features, or plan
                                structure at any time in accordance with clause
                                4 above, and such changes will apply from the
                                next billing period after notice is provided.
                            </p>
                        </section>

                        {/* 14. Contact */}
                        <section className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white shadow-lg sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-bold text-gray-900">
                                    14
                                </span>
                                Contact
                            </h2>
                            <p className="mb-4 text-gray-200">
                                For questions or support, please contact us:
                            </p>
                            <div className="space-y-2 rounded-xl bg-white/10 p-6 backdrop-blur-sm">
                                <p className="text-lg font-bold">
                                    Softwear Labs Pty Ltd
                                </p>
                                <p className="text-gray-200">
                                    Suite 2, Level 3 7-15 Macquarie Place
                                    <br />
                                    Sydney 2000 NSW, Australia
                                </p>
                                <p className="pt-2">
                                    <span className="text-gray-300">
                                        Email:{' '}
                                    </span>
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="font-medium text-blue-300 underline hover:text-blue-200"
                                    >
                                        support@wtf.style
                                    </a>
                                </p>
                            </div>
                        </section>

                        {/* Footer */}
                        <div className="border-t border-gray-200 pt-8 pb-4">
                            <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-600">
                                By using What The F*** Are You Wearing, you
                                agree to these Terms and Conditions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
